/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { parseDate } from '@internationalized/date';
import { format, parse } from 'date-fns';

import {
    Box,
    Heading,
    Spacer,
    Inline,
    DateRangePicker,
    Select,
    SelectOption,
    Carousel,
    useBreakpoint,
    Button,
    Icon,
    SearchActions,
    localToday,
    calendarDateToUtcString,
} from 'sarsaparilla';
import { updateSearchCriterias, updateSelectedDate } from 'ui-search/src/actions/search';
import { fetchCampingSearch } from '../redux/actions/fetchSearch';
import { fetchCampingSearchSelector } from '../redux/reducers/fetchSearch';
import CampingDayUseItem from '../components/CampingDayUseItem';

const propTypes = {
    location: PropTypes.shape({
        locationId: PropTypes.string,
        name: PropTypes.string,
    }),
};

function calendarDateToWeirdSearchFormat(date) {
    const addLeadingZero = (num) => {
        if (num < 10) return `0${num}`;
        return `${num}`;
    };
    return `${addLeadingZero(date.month)}/${addLeadingZero(date.day)}/${date.year}`;
}

const SORT_BY_AVAILABLE = 'available';
const SORT_BY_BEST_MATCH = 'score';

export default function CampingDayUse({ location }) {
    const { isXlAndUp } = useBreakpoint();
    const dispatch = useDispatch();
    const searchCheckIn = useSelector((state) => state.search?.checkin_time);
    const searchCheckOut = useSelector((state) => state.search?.checkout_time);
    const search = useSelector(fetchCampingSearchSelector);
    const [dateRange, setDateRange] = useState(() => {
        if (searchCheckIn && searchCheckOut) {
            const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
            if (!dateRegex.test(searchCheckIn) || !dateRegex.test(searchCheckOut)) {
                return { start: null, end: null };
            }
            const startDate = format(
                parse(searchCheckIn, 'MM/dd/yyyy', new Date()),
                'yyyy-MM-dd'
            );
            const endDate = format(
                parse(searchCheckOut, 'MM/dd/yyyy', new Date()),
                'yyyy-MM-dd'
            );
            return {
                start: parseDate(startDate),
                end: parseDate(endDate),
            };
        }
        return { start: null, end: null };
    });
    const hasDateRange = dateRange?.start && dateRange?.end;
    const [sortBy, setSortBy] = useState(
        hasDateRange ? SORT_BY_AVAILABLE : SORT_BY_BEST_MATCH
    );
    const sortByOptions = [
        { value: SORT_BY_BEST_MATCH, title: 'Best Match' },
        { value: SORT_BY_AVAILABLE, title: 'Available' },
        { value: 'distance', title: 'Distance' },
        { value: 'average_rating', title: 'Rating' },
        { value: 'price asc', title: 'Price' },
        { value: 'aggregate_cell_coverage', title: 'Mobile Coverage' },
    ].filter((item) => {
        const isAvailable = item?.value === SORT_BY_AVAILABLE;

        if (isAvailable && !hasDateRange) {
            return false;
        }

        return true;
    });

    useEffect(() => {
        dispatch(
            fetchCampingSearch({
                locationId: location.locationId,
                sort: sortBy,
                startDate: calendarDateToUtcString(dateRange.start),
                endDate: calendarDateToUtcString(dateRange.end),
            })
        );
    }, [location.locationId, sortBy, dateRange, dispatch]);

    if (!search?.data?.size) {
        return null;
    }

    return (
        <Box paddingY="xl" id={'camping-day-use-wrapper-id'}>
            <Heading headingLevel={2} appearance="h3">
                Camping & Day Use
            </Heading>
            <Spacer size="gutter" />
            <Inline>
                <DateRangePicker
                    label="Check-in and Check-out Dates"
                    startName="checkIn"
                    endName="checkOut"
                    minValue={localToday}
                    value={hasDateRange ? dateRange : null}
                    onChange={(value) => {
                        if (value) {
                            setSortBy(SORT_BY_AVAILABLE);
                            setDateRange(value);
                            dispatch(
                                updateSelectedDate(
                                    calendarDateToWeirdSearchFormat(value.start),
                                    true
                                )
                            );
                            dispatch(
                                updateSelectedDate(
                                    calendarDateToWeirdSearchFormat(value.end),
                                    false
                                )
                            );
                        } else {
                            dispatch(
                                updateSearchCriterias({
                                    checkin_time: null,
                                    checkout_time: null,
                                })
                            );
                            setSortBy(SORT_BY_BEST_MATCH);
                            setDateRange({ start: null, end: null });
                        }
                    }}
                />

                <Select
                    onChange={(e) => setSortBy(e.target.value)}
                    value={sortBy}
                    label="Sort By"
                    id="camping-search-sortby"
                >
                    {sortByOptions.map(({ value, title }) => (
                        <SelectOption value={value} key={value}>
                            {title}
                        </SelectOption>
                    ))}
                </Select>
            </Inline>
            <Spacer size="sm" />
            {search.data?.results?.length > 0 && (
                <Carousel
                    slideTargetWidth={275}
                    hasNegativeMargins={isXlAndUp}
                    hasPaddingBelow
                    hasGutters
                >
                    {search.data.results.map((result) => (
                        <CampingDayUseItem searchResult={result} key={result.id} />
                    ))}
                </Carousel>
            )}
            <Button
                appearance="link"
                href={SearchActions.buildSearchNavUri('/search', {
                    search: {
                        what: location.name,
                        entity_id: location.locationId,
                        entity_type: 'recarea',
                        parent_asset_id: location.locationId,
                        inventory_type: ['camping', 'dayuse'],
                        checkin_time: dateRange?.start
                            ? calendarDateToWeirdSearchFormat(dateRange.start)
                            : '',
                        checkout_time: dateRange.end
                            ? calendarDateToWeirdSearchFormat(dateRange.end)
                            : '',
                    },
                })}
                iconAfterElement={<Icon iconName="arrow-forward" />}
            >
                View all
            </Button>
        </Box>
    );
}

CampingDayUse.propTypes = propTypes;

// cSpell:ignore checkin, recarea, dayuse
